/**
 * Full-page error state shown when a scope-seeding layout (customer / plant)
 * cannot load the context it needs to render its child routes.
 *
 * Replaces the endless shell loader a failed scope-seed fetch used to leave
 * behind: a denied or unreachable scope now degrades to a recoverable message
 * with a retry affordance, mirroring the site-dashboard tier's load-error
 * treatment.
 *
 * Contains:
 * - ScopeLoadError — presentational error + retry component
 */
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {{ onRetry: () => void }} props
 *   onRetry — re-triggers the scope-seed fetch.
 */
function ScopeLoadError({ onRetry }) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Alert
        severity="error"
        sx={{ maxWidth: 480 }}
        action={
          <Button size="small" color="inherit" onClick={onRetry}>
            {t('actions.retry', 'Retry')}
          </Button>
        }
      >
        {t('general.scopeLoadError', "Couldn't load this page. Check your connection and try again.")}
      </Alert>
    </Box>
  );
}

export default ScopeLoadError;
